/**
 * Chunk Loading Error Handler
 *
 * Handles dynamic import failures caused by stale service worker caches
 * or deployment mismatches. When a chunk fails to load, it:
 * 1. Clears all service worker caches
 * 2. Unregisters stale service workers
 * 3. Force-reloads the page (once) to fetch fresh assets
 *
 * This is the primary fix for the "Persistent White Screen" issue (#2181)
 * where stale cached assets cause chunk loading failures after deployments.
 */

const RELOAD_KEY = 'chunk-error-reload';
const RELOAD_EXPIRY_MS = 30_000; // 30 seconds - prevent infinite reload loops

/**
 * Check if we recently reloaded due to a chunk error.
 * Prevents infinite reload loops if the error persists.
 */
const hasRecentReload = (): boolean => {
  try {
    const lastReload = sessionStorage.getItem(RELOAD_KEY);
    if (!lastReload) return false;

    const elapsed = Date.now() - parseInt(lastReload, 10);
    return elapsed < RELOAD_EXPIRY_MS;
  } catch {
    return false;
  }
};

/**
 * Mark that we're about to reload due to a chunk error.
 */
const markReload = (): void => {
  try {
    sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
  } catch {
    // sessionStorage may not be available in private mode
  }
};

/**
 * Clear all service worker caches and unregister workers.
 */
const clearServiceWorkerCaches = async (): Promise<void> => {
  try {
    // Delete all caches
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));

    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    console.log('[ChunkErrorHandler] Cleared all SW caches and unregistered workers');
  } catch (error) {
    console.error('[ChunkErrorHandler] Failed to clear caches:', error);
  }
};

/**
 * Determines if an error is a chunk/dynamic-import loading failure.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;

  const message = error instanceof Error ? error.message : String(error);
  const chunkErrorPatterns = [
    'Loading chunk',
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'error loading dynamically imported module',
    'Unable to preload CSS',
    'ChunkLoadError',
    // MIME type mismatch when SW serves stale HTML instead of JS
    "'text/html' is not a valid JavaScript MIME type",
    'Failed to load module script',
  ];

  return chunkErrorPatterns.some((pattern) =>
    message.toLowerCase().includes(pattern.toLowerCase()),
  );
};

/**
 * Handle a chunk loading error by clearing caches and reloading.
 * Returns true if recovery was initiated (reload scheduled).
 */
export const handleChunkError = async (error: unknown): Promise<boolean> => {
  if (!isChunkLoadError(error)) {
    return false;
  }

  console.warn('[ChunkErrorHandler] Chunk load error detected:', error);

  // Prevent infinite reload loop
  if (hasRecentReload()) {
    console.error(
      '[ChunkErrorHandler] Already reloaded recently, not reloading again. ' +
        'The user may need to manually clear their browser cache.',
    );
    return false;
  }

  // Clear stale caches, mark reload, and force-reload
  await clearServiceWorkerCaches();
  markReload();

  console.log('[ChunkErrorHandler] Reloading page to fetch fresh assets...');
  window.location.reload();

  return true;
};

/**
 * Install a global unhandled error listener for chunk load failures.
 * This catches errors from lazy routes that bubble up as unhandled rejections.
 */
export const installChunkErrorListener = (): void => {
  window.addEventListener('unhandledrejection', async (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      await handleChunkError(event.reason);
    }
  });

  window.addEventListener('error', async (event) => {
    if (isChunkLoadError(event.error || event.message)) {
      event.preventDefault();
      await handleChunkError(event.error || event.message);
    }
  });
};
